import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { ADMIN_WELCOME_PATH } from '@/config/adminNavigation'
import { getMenuRouteItems } from '@/config/adminMenus'
import pinia from '@/stores'
import { useAuthStore } from '@/stores/auth'
import { reloadApplication } from '@/utils/applicationReload'

const pageComponents = {
  'business-user-list': () => import('@/views/business/clientUsers/index.vue'),
  'business-coin-records': () => import('@/views/business/transactions/index.vue'),
  'business-cash-records': () => import('@/views/business/transactions/index.vue'),
  'business-withdrawal-records': () => import('@/views/business/withdrawals/index.vue'),
  'business-ticket-records': () => import('@/views/business/transactions/index.vue'),
  'business-message-records': () => import('@/views/business/messages/index.vue'),
  'business-sent-message-records': () => import('@/views/business/messages/sentList.vue'),
  'business-casino-list': () => import('@/views/business/odds/index.vue'),
  'business-payment-methods': () => import('@/views/business/odds/paymentWays.vue'),
  'business-news-notices': () => import('@/views/business/config/newsNotices/index.vue'),
  'business-casino-licenses': () => import('@/views/business/odds/licenses.vue'),
  'business-bonus-list': () => import('@/views/business/bonuses/index.vue'),
  'business-demo-games': () => import('@/views/business/games/demoGames.vue'),
  'business-coin-games': () => import('@/views/business/games/coinGames.vue'),
  'business-game-providers': () => import('@/views/business/games/providers.vue'),
  'business-game-categories': () => import('@/views/business/games/categories.vue'),
  'business-game-features': () => import('@/views/business/games/features.vue'),
  'business-game-atlases': () => import('@/views/business/games/atlases.vue'),
  'business-game-atlas-categories': () => import('@/views/business/games/atlasCategories.vue'),
  'country-site-weekly-tournaments': () => import('@/views/business/activities/weeklyTournaments/index.vue'),
  'country-site-daily-tournaments': () => import('@/views/business/activities/weeklyTournaments/index.vue'),
  'country-site-multiplier-challenges': () => import('@/views/countrySite/activities/multiplierChallenges/index.vue'),
  'business-posts': () => import('@/views/business/content/posts/index.vue'),
  'business-comments': () => import('@/views/business/content/comments/index.vue'),
  'business-topics': () => import('@/views/business/content/topics/index.vue'),
  'business-odds-ratings': () => import('@/views/business/content/reviews/index.vue'),
  'business-game-ratings': () => import('@/views/business/content/reviews/games.vue'),
  'business-articles': () => import('@/views/business/content/news/articles.vue'),
  'business-newsletters': () => import('@/views/business/content/news/newsletters/index.vue'),
  'business-news-topics': () => import('@/views/business/content/news/topics/index.vue'),
  'business-news-authors': () => import('@/views/business/content/news/authors.vue'),
  'business-news-feedbacks': () => import('@/views/business/content/news/feedbacks.vue'),
  'business-blocked-users': () => import('@/views/business/content/blocked-users/index.vue'),
  'business-post-audits': () => import('@/views/business/audits/posts/index.vue'),
  'business-comment-audits': () => import('@/views/business/audits/comments/index.vue'),
  'business-game-rating-audits': () => import('@/views/business/audits/gameRatings/index.vue'),
  'business-odds-audits': () => import('@/views/business/audits/oddsRatings/index.vue'),
  'business-withdrawal-audits': () => import('@/views/business/audits/withdrawals/index.vue'),
  'business-fb-share-audits': () => import('@/views/business/audits/fbShares/index.vue'),
  'business-complaint-audits': () => import('@/views/business/audits/complaints/index.vue'),
  'business-countries': () => import('@/views/business/masterData/countries.vue'),
  'business-languages': () => import('@/views/business/masterData/languages.vue'),
  'business-currencies': () => import('@/views/business/masterData/currencies.vue'),
  'business-exchange-rates': () => import('@/views/business/masterData/exchangeRates.vue'),
  'business-responsible-gambling': () => import('@/views/business/masterData/responsibleGambling.vue'),
  'business-characters': () => import('@/views/business/authorConfigs/index.vue'),
  'business-lucky-wheel': () => import('@/views/business/configuration/luckyWheel.vue'),
  'business-slot-machine': () => import('@/views/business/configuration/slotMachine.vue'),
  'business-lucky-scratch': () => import('@/views/business/configuration/luckyScratch.vue'),
  'business-coin-shop-exchanges': () => import('@/views/business/configuration/coinShopExchanges.vue'),
  'business-project-global-config': () => import('@/views/business/configuration/projectGlobal.vue'),
  'business-invitee-tasks': () => import('@/views/business/configuration/inviteeTasks.vue'),
  'business-payout-payment-ways': () => import('@/views/business/configuration/payoutPaymentWays.vue'),
  'country-site-list': () => import('@/views/countrySite/sites/index.vue'),
  'country-site-odds': () => import('@/views/countrySite/odds/index.vue'),
  'country-site-odds-activities': () => import('@/views/countrySite/oddsActivities/index.vue'),
  'country-site-country-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-odds-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-casino-app-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-bonus-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-game-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-game-provider-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-complaint-channel-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-freecash-channel-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-community-channel-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-review-channel-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-tools-channel-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-play-for-real-money-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-game-theme-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-people-routes': () => import('@/views/countrySite/pages/index.vue'),
  'country-site-page-center': () => import('@/views/countrySite/pageManagement/index.vue'),
  'country-site-sitemap': () => import('@/views/countrySite/sitemap/index.vue'),
  'country-site-route-groups': () => import('@/views/countrySite/routeGroups/index.vue'),
  'country-site-social-media': () => import('@/views/countrySite/socialMedia/index.vue'),
  'country-site-login-page-image': () => import('@/views/countrySite/loginPageImage/index.vue'),
  'country-site-faq-list': () => import('@/views/countrySite/faq/index.vue'),
  'country-site-footer-config': () => import('@/views/countrySite/footer/index.vue'),
  'country-site-game-config': () => import('@/views/countrySite/games/index.vue'),
  'country-site-game-classifications': () => import('@/views/countrySite/gameClassifications/index.vue'),
  'country-site-provider-config': () => import('@/views/countrySite/gameProviders/index.vue'),
  'business-game-ip-topics': () => import('@/views/business/games/ipTopics/index.vue'),
  'country-site-game-topics': () => import('@/views/countrySite/gameTopics/index.vue'),
  'business-contact-feedback-records': () => import('@/views/countrySite/contactFeedback/index.vue'),
  'country-site-author-config': () => import('@/views/countrySite/authors/index.vue'),
  'system-users': () => import('@/views/system/users/index.vue'),
  'system-roles': () => import('@/views/system/roles/index.vue'),
  'system-menus': () => import('@/views/system/menus/index.vue'),
  'system-operation-logs': () => import('@/views/business/system/operationLogs/index.vue')
}

const menuPlaceholder = () => import('@/views/menuPlaceholder/index.vue')

const prototypeMenuRoutes: RouteRecordRaw[] = getMenuRouteItems().map(({ scope, page, parentTitle }) => ({
  path: page.path!.replace(/^\//, ''),
  name: page.key,
  component: pageComponents[page.key as keyof typeof pageComponents] || menuPlaceholder,
  meta: {
    title: page.title,
    parentTitle: parentTitle || (scope === 'countrySite' ? '国家站点管理' : undefined),
    system: 'business',
    menuCode: page.menuCode,
    pageType: page.pageType,
    tournamentKind: page.tournamentKind
  }
}))

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/login/index.vue'),
      meta: { title: '登录', public: true }
    },
    {
      path: '/',
      component: AdminLayout,
      children: [
        {
          path: '',
          redirect: ADMIN_WELCOME_PATH
        },
        {
          path: 'welcome',
          name: 'welcome',
          component: () => import('@/views/home/index.vue'),
          meta: { title: '欢迎页' }
        },
        {
          path: 'business/records/messages',
          redirect: '/business/config/message-templates'
        },
        {
          path: 'business/content/characters',
          redirect: '/business/config/characters'
        },
        {
          path: 'business/content/ratings',
          redirect: '/business/content/odds-ratings'
        },
        {
          path: 'business/audits/ratings',
          redirect: '/business/audits/game-ratings'
        },
        {
          path: 'country-site/activities/weekly-tournaments/:tournamentId/edit',
          name: 'country-site-weekly-tournament-edit',
          component: () => import('@/views/business/activities/weeklyTournaments/formPage.vue'),
          meta: {
            title: '编辑Weekly锦标赛',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:weekly-tournament',
            activeMenu: '/country-site/activities/weekly-tournaments',
            tournamentKind: 'weekly'
          }
        },
        {
          path: 'country-site/activities/weekly-tournaments/:tournamentId/participants',
          name: 'country-site-weekly-tournament-participants',
          component: () => import('@/views/business/activities/weeklyTournaments/participantsPage.vue'),
          meta: {
            title: 'Weekly锦标赛参与详情',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:weekly-tournament',
            activeMenu: '/country-site/activities/weekly-tournaments',
            tournamentKind: 'weekly'
          }
        },
        {
          path: 'country-site/activities/weekly-tournaments/templates',
          name: 'country-site-weekly-tournament-templates',
          component: () => import('@/views/business/activities/weeklyTournaments/templatePage.vue'),
          meta: {
            title: 'Weekly锦标赛模板',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:weekly-tournament',
            activeMenu: '/country-site/activities/weekly-tournaments',
            tournamentKind: 'weekly'
          }
        },
        {
          path: 'country-site/activities/daily-tournaments/:tournamentId/edit',
          name: 'country-site-daily-tournament-edit',
          component: () => import('@/views/business/activities/weeklyTournaments/formPage.vue'),
          meta: {
            title: '编辑Daily锦标赛',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:daily-tournament',
            activeMenu: '/country-site/activities/daily-tournaments',
            tournamentKind: 'daily'
          }
        },
        {
          path: 'country-site/activities/daily-tournaments/:tournamentId/participants',
          name: 'country-site-daily-tournament-participants',
          component: () => import('@/views/business/activities/weeklyTournaments/participantsPage.vue'),
          meta: {
            title: 'Daily锦标赛参与详情',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:daily-tournament',
            activeMenu: '/country-site/activities/daily-tournaments',
            tournamentKind: 'daily'
          }
        },
        {
          path: 'country-site/activities/daily-tournaments/templates',
          name: 'country-site-daily-tournament-templates',
          component: () => import('@/views/business/activities/weeklyTournaments/templatePage.vue'),
          meta: {
            title: 'Daily锦标赛模板',
            parentTitle: '活动管理',
            system: 'business',
            menuCode: 'country-site:activity:daily-tournament',
            activeMenu: '/country-site/activities/daily-tournaments',
            tournamentKind: 'daily'
          }
        },
        { path: 'business/activities/weekly-tournaments', redirect: '/country-site/activities/weekly-tournaments' },
        { path: 'business/activities/daily-tournaments', redirect: '/country-site/activities/daily-tournaments' },
        { path: 'business/activities/multiplier-challenges', redirect: '/country-site/activities/multiplier-challenges' },
        {
          path: 'business/bonuses/create',
          name: 'business-bonus-create',
          component: () => import('@/views/business/bonuses/formPage.vue'),
          meta: {
            title: '新增活动',
            parentTitle: '礼金管理',
            system: 'business',
            menuCode: 'center:odds-activity:query',
            activeMenu: '/business/bonuses'
          }
        },
        {
          path: 'business/bonuses/:activityId/edit',
          name: 'business-bonus-edit',
          component: () => import('@/views/business/bonuses/formPage.vue'),
          meta: {
            title: '编辑活动',
            parentTitle: '礼金管理',
            system: 'business',
            menuCode: 'center:odds-activity:query',
            activeMenu: '/business/bonuses'
          }
        },
        {
          path: 'business/casinos/create',
          name: 'business-casino-create',
          component: pageComponents['business-casino-list'],
          meta: {
            title: '新增盘口',
            parentTitle: '盘口管理',
            system: 'business',
            menuCode: 'center:odds:query',
            activeMenu: '/business/casinos',
            formMode: 'add'
          }
        },
        {
          path: 'business/casinos/:recordId/edit',
          name: 'business-casino-edit',
          component: pageComponents['business-casino-list'],
          meta: {
            title: '编辑盘口',
            parentTitle: '盘口管理',
            system: 'business',
            menuCode: 'center:odds:query',
            activeMenu: '/business/casinos',
            formMode: 'edit'
          }
        },
        {
          path: 'business/games/demo/create',
          name: 'business-demo-game-create',
          component: pageComponents['business-demo-games'],
          meta: {
            title: '添加游戏',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:demo-game:query',
            activeMenu: '/business/games/demo',
            formMode: 'add'
          }
        },
        {
          path: 'business/games/demo/:recordId/edit',
          name: 'business-demo-game-edit',
          component: pageComponents['business-demo-games'],
          meta: {
            title: '编辑Demo游戏',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:demo-game:query',
            activeMenu: '/business/games/demo',
            formMode: 'edit'
          }
        },
        {
          path: 'business/games/coin/create',
          name: 'business-coin-game-create',
          component: pageComponents['business-coin-games'],
          meta: {
            title: '添加游戏',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:coin-game:query',
            activeMenu: '/business/games/coin',
            formMode: 'add'
          }
        },
        {
          path: 'business/games/coin/:recordId/edit',
          name: 'business-coin-game-edit',
          component: pageComponents['business-coin-games'],
          meta: {
            title: '编辑金币游戏',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:coin-game:query',
            activeMenu: '/business/games/coin',
            formMode: 'edit'
          }
        },
        {
          path: 'business/games/atlases/create',
          name: 'business-game-atlas-create',
          component: pageComponents['business-game-atlases'],
          meta: {
            title: '新增游戏图鉴',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:game-atlas:query',
            activeMenu: '/business/games/atlases',
            formMode: 'add'
          }
        },
        {
          path: 'business/games/atlases/:recordId/edit',
          name: 'business-game-atlas-edit',
          component: pageComponents['business-game-atlases'],
          meta: {
            title: '编辑游戏图鉴',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:game-atlas:query',
            activeMenu: '/business/games/atlases',
            formMode: 'edit'
          }
        },
        {
          path: 'country-site/odds-activities/create',
          name: 'country-site-odds-activity-create',
          component: () => import('@/views/countrySite/oddsActivities/formPage.vue'),
          meta: {
            title: '新增Bonus',
            parentTitle: '国家站点管理',
            system: 'business',
            menuCode: 'site:odds-activity:query',
            activeMenu: '/country-site/odds-activities'
          }
        },
        {
          path: 'country-site/odds/:recordId/edit',
          name: 'country-site-odds-edit',
          component: pageComponents['country-site-odds'],
          meta: {
            title: '编辑站点盘口',
            parentTitle: '国家站点管理',
            system: 'business',
            menuCode: 'site:odds:query',
            activeMenu: '/country-site/odds',
            formMode: 'edit'
          }
        },
        {
          path: 'country-site/games/:recordId/edit',
          name: 'country-site-game-edit',
          component: () => import('@/views/countrySite/games/formPage.vue'),
          meta: {
            title: '编辑站点游戏',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'site:demo-game:config',
            activeMenu: '/country-site/games',
            formMode: 'edit'
          }
        },
        {
          path: 'country-site/providers/:recordId/edit',
          name: 'country-site-game-provider-edit',
          component: () => import('@/views/countrySite/gameProviders/formPage.vue'),
          meta: {
            title: '配置站点供应商',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'country-site:provider:config',
            activeMenu: '/country-site/providers'
          }
        },
        {
          path: 'business/games/ip-topics/create',
          name: 'business-game-ip-topic-create',
          component: () => import('@/views/business/games/ipTopics/formPage.vue'),
          meta: {
            title: '添加游戏专题',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:demo-game-topic:query',
            activeMenu: '/business/games/ip-topics'
          }
        },
        {
          path: 'business/games/ip-topics/:recordId/edit',
          name: 'business-game-ip-topic-edit',
          component: () => import('@/views/business/games/ipTopics/formPage.vue'),
          meta: {
            title: '编辑游戏IP主题',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'center:demo-game-topic:query',
            activeMenu: '/business/games/ip-topics'
          }
        },
        {
          path: 'country-site/game-topics/create',
          redirect: '/country-site/game-topics'
        },
        {
          path: 'country-site/game-topics/:recordId/edit',
          name: 'country-site-game-ip-topic-edit',
          component: () => import('@/views/countrySite/gameTopics/formPage.vue'),
          meta: {
            title: '编辑游戏IP主题',
            parentTitle: '游戏管理',
            system: 'business',
            menuCode: 'site:demo-game:topic',
            activeMenu: '/country-site/game-topics'
          }
        },
        {
          path: 'country-site/social-media/create',
          name: 'country-site-social-media-create',
          component: () => import('@/views/countrySite/socialMedia/formPage.vue'),
          meta: {
            title: '新增社媒',
            parentTitle: '数据配置',
            system: 'business',
            menuCode: 'site:social-media:query',
            activeMenu: '/country-site/social-media'
          }
        },
        {
          path: 'country-site/social-media/:recordId/edit',
          name: 'country-site-social-media-edit',
          component: () => import('@/views/countrySite/socialMedia/formPage.vue'),
          meta: {
            title: '编辑社媒',
            parentTitle: '数据配置',
            system: 'business',
            menuCode: 'site:social-media:query',
            activeMenu: '/country-site/social-media'
          }
        },
        {
          path: 'country-site/faq-list/create',
          name: 'country-site-faq-create',
          component: () => import('@/views/countrySite/faq/formPage.vue'),
          meta: {
            title: '新增FAQ',
            parentTitle: '数据配置',
            system: 'business',
            menuCode: 'site:faq:query',
            activeMenu: '/country-site/faq-list'
          }
        },
        {
          path: 'country-site/faq-list/:recordId/edit',
          name: 'country-site-faq-edit',
          component: () => import('@/views/countrySite/faq/formPage.vue'),
          meta: {
            title: '编辑FAQ',
            parentTitle: '数据配置',
            system: 'business',
            menuCode: 'site:faq:query',
            activeMenu: '/country-site/faq-list'
          }
        },
        {
          path: 'business/system/roles/create',
          name: 'system-role-create',
          component: pageComponents['system-roles'],
          meta: {
            title: '新增角色',
            parentTitle: '系统管理',
            system: 'business',
            menuCode: 'center:role:manage',
            activeMenu: '/business/system/roles',
            formMode: 'add'
          }
        },
        {
          path: 'business/system/roles/:recordId/edit',
          name: 'system-role-edit',
          component: pageComponents['system-roles'],
          meta: {
            title: '编辑角色',
            parentTitle: '系统管理',
            system: 'business',
            menuCode: 'center:role:manage',
            activeMenu: '/business/system/roles',
            formMode: 'edit'
          }
        },
        {
          path: 'country-site/odds-activities/:activityId/edit',
          name: 'country-site-odds-activity-edit',
          component: () => import('@/views/countrySite/oddsActivities/formPage.vue'),
          meta: {
            title: '编辑Bonus',
            parentTitle: '国家站点管理',
            system: 'business',
            menuCode: 'site:odds-activity:query',
            activeMenu: '/country-site/odds-activities'
          }
        },
        {
          path: 'business/content/posts/create',
          name: 'business-post-create',
          component: () => import('@/views/business/content/posts/formPage.vue'),
          meta: {
            title: '新增帖子',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'site:post:query',
            activeMenu: '/business/content/posts'
          }
        },
        {
          path: 'business/content/posts/:postId/comments',
          name: 'business-post-comments',
          component: () => import('@/views/business/content/posts/commentsPage.vue'),
          meta: {
            title: '帖子评论',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'site:post:query',
            activeMenu: '/business/content/posts'
          }
        },
        {
          path: 'business/content/posts/:postId/edit',
          name: 'business-post-edit',
          component: () => import('@/views/business/content/posts/formPage.vue'),
          meta: {
            title: '编辑帖子',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'site:post:query',
            activeMenu: '/business/content/posts'
          }
        },
        {
          path: 'business/content/articles/create',
          name: 'business-article-create',
          component: () => import('@/views/business/content/news/articleForm.vue'),
          meta: {
            title: '新增文章',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'center:news-article:query',
            activeMenu: '/business/content/articles'
          }
        },
        {
          path: 'business/content/articles/:id/edit',
          name: 'business-article-edit',
          component: () => import('@/views/business/content/news/articleForm.vue'),
          meta: {
            title: '编辑文章',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'center:news-article:query',
            activeMenu: '/business/content/articles'
          }
        },
        {
          path: 'business/content/newsletters/create',
          name: 'business-newsletter-create',
          component: () => import('@/views/business/content/news/newsletters/form.vue'),
          meta: { title: '新建每周简报', parentTitle: '内容管理', system: 'business', menuCode: 'center:newsletter:query', activeMenu: '/business/content/newsletters' }
        },
        {
          path: 'business/content/newsletters/:id/edit',
          name: 'business-newsletter-edit',
          component: () => import('@/views/business/content/news/newsletters/form.vue'),
          meta: { title: '编辑每周简报', parentTitle: '内容管理', system: 'business', menuCode: 'center:newsletter:query', activeMenu: '/business/content/newsletters' }
        },
        {
          path: 'business/content/newsletters/:id/deliveries',
          name: 'business-newsletter-deliveries',
          component: () => import('@/views/business/content/news/newsletters/deliveries.vue'),
          meta: { title: '发送详情', parentTitle: '内容管理', system: 'business', menuCode: 'center:newsletter:query', activeMenu: '/business/content/newsletters' }
        },
        {
          path: 'business/content/news-topics/create',
          name: 'business-news-topic-create',
          component: () => import('@/views/business/content/news/topics/form.vue'),
          meta: {
            title: '新增新闻专题',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'center:news-topic:query',
            activeMenu: '/business/content/news-topics'
          }
        },
        {
          path: 'business/content/news-topics/:id/edit',
          name: 'business-news-topic-edit',
          component: () => import('@/views/business/content/news/topics/form.vue'),
          meta: {
            title: '编辑新闻专题',
            parentTitle: '内容管理',
            system: 'business',
            menuCode: 'center:news-topic:query',
            activeMenu: '/business/content/news-topics'
          }
        },
        {
          path: 'business/content/news-authors/create',
          name: 'business-news-author-create',
          component: () => import('@/views/business/content/news/authorForm.vue'),
          meta: { title: '新增新闻作者', parentTitle: '内容管理', system: 'business', menuCode: 'center:news-author:query', activeMenu: '/business/content/news-authors' }
        },
        {
          path: 'business/content/news-authors/:id/edit',
          name: 'business-news-author-edit',
          component: () => import('@/views/business/content/news/authorForm.vue'),
          meta: { title: '编辑新闻作者', parentTitle: '内容管理', system: 'business', menuCode: 'center:news-author:query', activeMenu: '/business/content/news-authors' }
        },
        {
          path: 'business/config/news-notices/create',
          name: 'business-news-notice-create',
          component: () => import('@/views/business/config/newsNotices/formPage.vue'),
          meta: {
            title: '新增新闻公告',
            parentTitle: '配置管理',
            system: 'business',
            menuCode: 'center:news-notice:query',
            activeMenu: '/business/config/news-notices'
          }
        },
        {
          path: 'business/config/news-notices/:id/edit',
          name: 'business-news-notice-edit',
          component: () => import('@/views/business/config/newsNotices/formPage.vue'),
          meta: {
            title: '编辑新闻公告',
            parentTitle: '配置管理',
            system: 'business',
            menuCode: 'center:news-notice:query',
            activeMenu: '/business/config/news-notices'
          }
        },
        {
          path: 'business/audits/posts/:postId/review',
          name: 'business-post-audit-review',
          component: () => import('@/views/business/audits/posts/reviewPage.vue'),
          meta: {
            title: '帖子审核',
            parentTitle: '审核管理',
            system: 'business',
            menuCode: 'site:post-review:query',
            activeMenu: '/business/audits/posts'
          }
        },
        {
          path: 'business/audits/comments/:commentId/review',
          name: 'business-comment-audit-review',
          component: () => import('@/views/business/audits/comments/reviewPage.vue'),
          meta: {
            title: '评论审核',
            parentTitle: '审核管理',
            system: 'business',
            menuCode: 'site:post-comment-review:query',
            activeMenu: '/business/audits/comments'
          }
        },
        {
          path: 'country-site/routes/player-wallet',
          redirect: '/country-site/routes/play-for-real-money'
        },
        {
          path: 'country-site/routes/game-ip-topics',
          redirect: '/country-site/routes/game-themes'
        },
        {
          path: 'country-site/routes/characters',
          redirect: '/country-site/routes/people'
        },
        // 这三类数据已合并到“游戏路由”展示，仅保留原地址的直接访问兼容，不再注册侧边栏菜单。
        {
          path: 'country-site/routes/game-providers',
          name: 'country-site-game-provider-routes',
          component: () => import('@/views/countrySite/pages/index.vue'),
          meta: {
            title: '供应商列表页',
            parentTitle: '路由配置',
            system: 'business',
            menuCode: 'country-site:route:game-providers',
            pageType: 'GAME_PROVIDERS',
            activeMenu: '/country-site/routes/games'
          }
        },
        {
          path: 'country-site/routes/play-for-real-money',
          name: 'country-site-play-for-real-money-routes',
          component: () => import('@/views/countrySite/pages/index.vue'),
          meta: {
            title: '玩真钱路由',
            parentTitle: '路由配置',
            system: 'business',
            menuCode: 'country-site:route:player-wallet',
            pageType: 'PLAY_FOR_REAL_MONEY',
            activeMenu: '/country-site/routes/games'
          }
        },
        {
          path: 'country-site/routes/game-themes',
          name: 'country-site-game-theme-routes',
          component: () => import('@/views/countrySite/pages/index.vue'),
          meta: {
            title: '主题游戏路由',
            parentTitle: '路由配置',
            system: 'business',
            menuCode: 'country-site:route:game-ip-topics',
            pageType: 'GAME_THEMES',
            activeMenu: '/country-site/routes/games'
          }
        },
        ...prototypeMenuRoutes
      ]
    },
    { path: '/:pathMatch(.*)*', redirect: '/' }
  ],
  scrollBehavior: () => ({ top: 0 })
})

const ROUTE_MODULE_RELOAD_KEY = 'ask-spinner-admin:route-module-reload'

router.onError((error, to) => {
  const isModuleLoadError =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      error.message
    )
  if (!isModuleLoadError) return

  const target = to.fullPath
  if (window.sessionStorage.getItem(ROUTE_MODULE_RELOAD_KEY) === target) {
    window.sessionStorage.removeItem(ROUTE_MODULE_RELOAD_KEY)
    return
  }
  window.sessionStorage.setItem(ROUTE_MODULE_RELOAD_KEY, target)
  reloadApplication(target)
})

router.beforeEach(async to => {
  const authStore = useAuthStore(pinia)

  if (!to.meta.public && !authStore.isAuthenticated) {
    return { name: 'login' }
  }

  if (to.name === 'login') {
    if (!authStore.isAuthenticated) return true

    try {
      await authStore.fetchCurrentUser()
      return ADMIN_WELCOME_PATH
    } catch {
      return true
    }
  }

  try {
    await authStore.fetchCurrentUser()
  } catch {
    return { name: 'login' }
  }
})

router.afterEach(to => {
  window.sessionStorage.removeItem(ROUTE_MODULE_RELOAD_KEY)
  const title = String(to.meta.title || import.meta.env.VITE_APP_TITLE || 'Ask Spinner 管理后台')
  document.title = `${title} - ${import.meta.env.VITE_APP_TITLE || 'Ask Spinner 管理后台'}`
})

export default router
